import {get, isEmpty, isNil} from 'lodash'
import themeVars from 'theme/themeVars'
import {round} from 'helpers/financial'
import {getPartPackFor, getPartPacks, updatePartPacksFor} from 'helpers/packs'
import {getPartPriceBreakMinPacks, getPreviousPartPriceBreak, setMinPacksToMaxPacks} from 'helpers/priceBreaks'
import {
  getPartSupplierFor,
  getPartSupplierForCompany,
  getPartSuppliers,
  updatePartSupplierFor,
} from 'helpers/partSuppliers'

export const getPartCostBreaksFor = (part, partPackId, partSupplierId) => {
  const partSupplier = getPartSupplierFor(part, partPackId, partSupplierId)
  const partCostBreaks = get(partSupplier, 'costs', [])
  return isEmpty(partCostBreaks) ? [] : partCostBreaks
}

export const getPartCostBreaksForCompany = (part, partPackId, companyId) => {
  const partSupplier = getPartSupplierForCompany(part, partPackId, companyId)
  const partCostBreaks = get(partSupplier, 'costs', [])
  return isEmpty(partCostBreaks) ? [] : partCostBreaks
}

export const createNewPartCostBreak = (part, partPackId, partSupplierId, isComplexPricingEnabled) => {
  const partPacks = getPartPacks(part, isComplexPricingEnabled)
  const partPack = getPartPackFor(partPackId, partPacks)
  const partCostBreaks = getPartCostBreaksFor(part, partPackId, partSupplierId)
  const minPacks = getPartPriceBreakMinPacks(partCostBreaks)
  const lastPartCostBreak = partCostBreaks[partCostBreaks.length - 1]

  const cost = part.msrpPricing
    ? get(lastPartCostBreak, 'cost', null)
    : get(partPack, 'standardCost', null)

  const discount = part.msrpPricing
    ? get(lastPartCostBreak, 'discount', null)
    : null

  return {
    id: -1,
    partSupplierId,
    minPacks,
    maxPacks: 0,
    cost,
    discount,
    guid: null,
  }
}

// Result is expressed in the part supplier's cost currency
export const getUnitCostForCostBreak = (part, pack, costBreak, costCurrency = null) => {
  if (costBreak.cost)
    return costBreak.cost

  if (part.msrpPricing) {
    const discount = costBreak.discount || 0
    const msrp = pack.msrp || 0
    return round(msrp - (msrp * discount / 100), 2)
  }

  const rate = get(costCurrency, 'rate', 1)

  if (!isNil(pack.standardCost))
    return round(pack.standardCost * rate, 2)  // Note: standardCost is expressed in the home currency

  return 0
}


export const addPartCostBreakFor = (part, partPackId, partSupplierId, isComplexPricingEnabled, partCostBreak) => {
  const updatedPart = {...part}
  const partPacks = getPartPacks(updatedPart, isComplexPricingEnabled)
  const partPack = getPartPackFor(partPackId, partPacks)

  partPack['suppliers'] = getPartSuppliers(partPack)
    .map(partSupplier => {
      if (partSupplier.id === partSupplierId) {

        let costs = get(partSupplier, 'costs', [])
        costs = isNil(costs) ? [] : costs
        costs.push(partCostBreak)

        let previousPartCostBreak = getPreviousPartPriceBreak(partCostBreak, costs)
        if (isNil(previousPartCostBreak)) {
          partSupplier['costs'] = costs
        } else {
          previousPartCostBreak = setMinPacksToMaxPacks(previousPartCostBreak)
          const previousPartCostBreakIndex = costs.findIndex(cost => cost.id === previousPartCostBreak.id)
          costs[previousPartCostBreakIndex] = previousPartCostBreak
          partSupplier['prices'] = costs
        }

        return partSupplier
      } else {
        return partSupplier
      }
    })
  updatedPart['packs'] = updatePartPacksFor(partPack.id, partPacks, partPack)
  return updatedPart
}

export const updatePartCostBreakFor = (part, partPackId, partSupplierId, isComplexPricingEnabled, partCostBreak) => {
  const updatedPart = {...part}
  const partPacks = getPartPacks(updatedPart, isComplexPricingEnabled)
  const partPack = getPartPackFor(partPackId, partPacks)
  const partSuppliers = getPartSuppliers(partPack)
  const supplier = partSuppliers.find(supplier => supplier.id === partSupplierId)

  supplier.costs = supplier.costs.map(cost => {
    return cost.id === partCostBreak.id
      ? partCostBreak
      : cost
  })

  return updatePartSupplierFor(part, partPackId, supplier, isComplexPricingEnabled)
}

export const deletePartCostBreakFor = (part, partPackId, partSupplierId, isComplexPricingEnabled, partCostBreak) => {
  const updatedPart = {...part}
  const partPacks = getPartPacks(updatedPart, isComplexPricingEnabled)
  const partPack = getPartPackFor(partPackId, partPacks)
  const partSuppliers = getPartSuppliers(partPack)
  const supplier = partSuppliers.find(supplier => supplier.id === partSupplierId)

  supplier.costs = supplier.costs.filter(cost => cost.id !== partCostBreak.id)
  supplier.deletedCosts = [partCostBreak]

  return updatePartSupplierFor(part, partPackId, supplier, isComplexPricingEnabled)
}


export const getCostColumnStyle = (partCostBreak) => {
  return isNil(partCostBreak.cost)
    ? {color: themeVars.palette.goodColor}
    : {color: themeVars.palette.primaryFontColor}
}

export const getDiscountColumnStyle = (partCostBreak) => {
  return isNil(partCostBreak.discount)
    ? {color: themeVars.palette.goodColor}
    : {color: themeVars.palette.primaryFontColor}
}

export const isDeletePartCostBreakEnabled = (part, partPackId, partSupplierId) => {
  return getPartCostBreaksFor(part, partPackId, partSupplierId).length > 1
}
